import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Sup, Text } from "@onnit-js/ui/components/text";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import MediaContentShowcase from "@onnit-js/ui/components/module/MediaContentShowcase";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import usePageData from "../../hooks/usePageData";
import config from "../../config/config";
import ingredients from "./ingredients";

// eslint-disable-next-line func-style
function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${config.IMG_URL}/product-page/new-mood/3.0`;
    const { IMG_PARAMS } = config;

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/2024-NM-Wpage-Gallery-1-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-NM-Wpage-Gallery-1-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="New MOOD product containers in the sky."
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/2024-NM-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            alt='New Mood packaging with description, "New Look, same trusted formula."'
                            width={920}
                            height={920}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/2024-NM-Wpage-Gallery-3-920x920.png${IMG_PARAMS}`}
                            alt="Woman performing yoga pose."
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/2024-NM-Wpage-Gallery-4-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-NM-Wpage-Gallery-4-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="Man and woman having a light conversation at dinner."
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.4" typeStyle="text02">
                                    New MOOD
                                    <Sup variant="reg" />
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Img
                                    src={`${IMG_URL}/2024-NM-Wpage-Balance.png${IMG_PARAMS}`}
                                    alt="Family doing homework at a table."
                                    width={592}
                                    height={804}
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                >
                                    New MOOD
                                    <Sup variant="reg" />
                                </Text>

                                <Text as="h2" color="calmPurple" typeStyle="title01" mb={2}>
                                    Balance Daily Stress
                                    <Sup variant="dagger" />
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 6" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" typeStyle="text02" color="grays.2">
                                    New MOOD
                                    <Sup variant="reg" /> is the amp antithesis, helping you<Box as="br" display={["none", "none", "block"]} />{" "}relax and focus on yourself by turning down the<Box as="br" display={["none", "none", "block"]} />{" "}noise of the chaos.
                                    <Sup variant="dagger" />
                                </Text>
                            </Box>
                            <Box gridColumn="3 / span 7" px={4}>
                                <Text as="h2" color="calmPurple" typeStyle="title01" mb={2}>
                                    New MOOD
                                    <Sup variant="reg" /> Is For:
                                </Text>
                                <List>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Busy professionals
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Students studying for exams
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="grays.2">
                                        Anyone going through occasional stressful times
                                        <Sup variant="dagger" />
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="onGray">
                                        People who want to chill
                                    </ListItem>
                                </List>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <TwelveColumn mb={4}>
                            <Box px={4} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                <Text as="h2" color="white" typeStyle="copyDisplay02">
                                    Key Ingredients
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <MediaContentShowcase
                            items={ingredients.map((item) => {
                                const copy = { ...item };
                                copy.image = `${IMG_URL}/${copy.image}${IMG_PARAMS}`;
                                return copy;
                            })}
                            activeColor="white"
                            textColor="grays.3"
                            bgMedia={(
                                <Img
                                    aria-hidden="true"
                                    placeholder={[754, 208]}
                                    src={`${IMG_URL}/2023-NM-Wpage-keyIngredients-background.png${IMG_PARAMS}`}
                                    alt="Mountain scenery."
                                />
                              )}
                        />
                    </Box>

                    <Box py={[6, 6, 6, 8]}>
                        <ImageContentPortrait
                            py={[6, 6, 6, 8]}
                            reverse
                            image={(
                                <Img
                                    alt="Woman in lotus pose"
                                    placeholder={[464, 600]}
                                    src={`${IMG_URL}/2024-NM-Wpage-How.png${IMG_PARAMS}`}
                                    width={926}
                                    height={1200}
                                />
                              )}
                            content={(
                                <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                    <Text as="h2" typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                        How New MOOD
                                        <Sup variant="reg" /> Works
                                    </Text>
                                    <Text as="p" typeStyle="text02" mb={[4, 4, 5]} color="grays.6">
                                        Serotonin has been shown to be a key neurotransmitter linked to mood, happiness,
                                        and positive outlook. New MOOD
                                        <Sup variant="reg" /> supports the body's serotonin levels by combining
                                        serotonin's two raw building blocks—L-tryptophan and 5-HTP—along with a converting catalyst,
                                        Vitamin B6.<Sup variant="dagger" />
                                    </Text>
                                    <Text as="p" typeStyle="text02" color="grays.6">
                                        New MOOD
                                        <Sup variant="reg" /> also features a host of herbs that help you maintain a
                                        state of peaceful calm, making it ideal for when you are feeling moments of stress,
                                        or just need some refreshing sleep.<Sup variant="dagger" />
                                    </Text>
                                </Box>
                              )}
                        />
                    </Box>
                </div>

                <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />}>
                    <Box>
                        <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" display="inline" mr={1}>
                            WARNING:
                        </Text>
                        <Text as="p" fontSize={0} display="inline" fontWeight="heavy" color="grays.4" mb={4}>
                            Consult a medical doctor before taking this or any other nutritional supplement if you are
                            pregnant, nursing, have or suspect a medical condition or are taking any medications. Store
                            in a cool, dry place. KEEP OUT OF REACH OF CHILDREN.
                        </Text>
                    </Box>
                    <Text as="p" fontSize={0} color="grays.4" fontWeight="bold" my={6}>
                        Albion
                        <Sup variant="reg" /> is a registered trademark of Balchem Corporation or its subsidiaries.
                    </Text>
                </UsageAndDisclaimers>
            </>
        </ThemeProvider>
    );
}

export default Page;
